// @givmo/board — reading the Schedule O draft (CBG-YR5-SCHO; the backend's
// `agent_docs/contracts/board-schedule-o.md` §5.2).
//
// The read serves, for one tax year, line 8b's entry (only where no committee is recorded) and line
// 11b's (while the return's surface is on): each a label, the sentences the backend built from the
// board's records (`text`, in the IRS e-file character set, shown and copied exactly as served), an
// IRS sentence quoted with its source, and the typed facts behind "From your records" — ids and days,
// never words.
//
// ⚠ THE TEXT AND THE IRS'S WORDS ARE PASSED THROUGH, never edited, trimmed or re-punctuated: what the
// page shows is what "Copy" copies (C9 and C10 as ruled), and the IRS's words are the IRS's (D-CBG-30).
//
// Tolerant, as every Board decode is: a field missing or of the wrong shape never throws. A fact row
// that cannot be read is dropped; an entry with no label reads as not served; a draft whose year end
// cannot be read reads as `null`, which the page shows as a failed read.

import type {
  BoardScheduleOConsentOut,
  BoardScheduleOCopyOut,
  BoardScheduleODraftOut,
  BoardScheduleOLine11bOut,
  BoardScheduleOLine8bOut,
  BoardScheduleOMeetingOut,
  BoardScheduleOReviewOut,
} from '@givmo/api-client';
import {
  decodeQuotation,
  decodeTaxYear,
  decodeYearEnd,
  type Filled,
  type Form990Quotation,
} from './form990-decode';

/** The quotation is never null on the wire; read tolerantly, an unreadable one is left out. */
export type ScheduleOLine8b = Omit<Filled<BoardScheduleOLine8bOut>, 'quotation'> & {
  quotation: Form990Quotation | null;
};
export type ScheduleODraft = Omit<Filled<BoardScheduleODraftOut>, 'line_8b'> & {
  line_8b: ScheduleOLine8b | null;
};
export type ScheduleOLine11b = Filled<BoardScheduleOLine11bOut>;
export type ScheduleOMeeting = Filled<BoardScheduleOMeetingOut>;
export type ScheduleOConsent = Filled<BoardScheduleOConsentOut>;
export type ScheduleOCopy = Filled<BoardScheduleOCopyOut>;
export type ScheduleOReview = Filled<BoardScheduleOReviewOut>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isDay(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY.test(value);
}

function textOrNull(value: unknown): string | null {
  return isText(value) ? value : null;
}

function dayOrNull(value: unknown): string | null {
  return isDay(value) ? value : null;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

function rows<T>(value: unknown, read: (row: Record<string, unknown>) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((row) => {
    if (!isObject(row)) return [];
    const decoded = read(row);
    return decoded === null ? [] : [decoded];
  });
}

function quotationOrNull(value: unknown): Form990Quotation | null {
  return isObject(value) ? decodeQuotation(value) : null;
}

function decodeMeeting(row: Record<string, unknown>): ScheduleOMeeting | null {
  if (!isText(row.meeting_id) || !isDay(row.held_on)) return null;
  return {
    meeting_id: row.meeting_id,
    held_on: row.held_on,
    minutes_record_id: textOrNull(row.minutes_record_id),
  };
}

function decodeConsent(row: Record<string, unknown>): ScheduleOConsent | null {
  if (!isText(row.consent_id) || !isDay(row.consent_date)) return null;
  return {
    consent_id: row.consent_id,
    consent_date: row.consent_date,
    record_id: textOrNull(row.record_id),
  };
}

function decodeLine8b(value: unknown): ScheduleOLine8b | null {
  if (!isObject(value) || !isText(value.label)) return null;
  return {
    label: value.label,
    quotation: quotationOrNull(value.quotation),
    text: textOrNull(value.text),
    meetings: rows(value.meetings, decodeMeeting),
    written_consents: rows(value.written_consents, decodeConsent),
  };
}

function decodeCopy(value: unknown): ScheduleOCopy | null {
  if (!isObject(value) || !isText(value.record_id) || !isDay(value.provided_on)) return null;
  return { record_id: value.record_id, provided_on: value.provided_on };
}

function decodeReview(value: unknown): ScheduleOReview | null {
  if (!isObject(value)) return null;
  const kind = value.kind;
  if (kind !== 'motion' && kind !== 'written_consent') return null;
  return {
    kind,
    meeting_id: textOrNull(value.meeting_id),
    held_on: dayOrNull(value.held_on),
    minutes_record_id: textOrNull(value.minutes_record_id),
    written_consent_id: textOrNull(value.written_consent_id),
    consent_date: dayOrNull(value.consent_date),
    record_id: textOrNull(value.record_id),
  };
}

function decodeLine11b(value: unknown): ScheduleOLine11b | null {
  if (!isObject(value) || !isText(value.label)) return null;
  return {
    label: value.label,
    text: textOrNull(value.text),
    quotation: quotationOrNull(value.quotation),
    copies_provided: count(value.copies_provided),
    copies_withdrawn: count(value.copies_withdrawn),
    copy_provided: decodeCopy(value.copy_provided),
    review: decodeReview(value.review),
  };
}

/**
 * The draft, or `null` when it cannot be read honestly: no year end, or a tax year that came but
 * can't be read (or none with a year end recorded or chosen — the Form 990 read's own rule).
 */
export function decodeScheduleODraft(body: unknown): ScheduleODraft | null {
  if (!isObject(body)) return null;
  const yearEnd = decodeYearEnd(body.year_end);
  if (yearEnd === null) return null;
  const taxYear = decodeTaxYear(body.tax_year);
  if (taxYear === null && (body.tax_year != null || yearEnd.source !== 'none')) return null;
  return {
    year_end: yearEnd,
    tax_years: rows(body.tax_years, (row) => decodeTaxYear(row)),
    tax_year: taxYear,
    form_year: typeof body.form_year === 'number' ? body.form_year : null,
    line_8b: taxYear === null ? null : decodeLine8b(body.line_8b),
    line_11b: taxYear === null ? null : decodeLine11b(body.line_11b),
    disclaimer_class_ids: Array.isArray(body.disclaimer_class_ids)
      ? body.disclaimer_class_ids.filter(isText)
      : [],
  };
}

/**
 * Line 11b's line in Board's voice when it has no sentence (§W1): no copy provided, every copy
 * withdrawn, or a copy that reached no director with no review recorded.
 */
export function line11bState(
  line: ScheduleOLine11b,
): 'no-copy' | 'every-copy-withdrawn' | 'reached-no-one' {
  if (line.copies_provided === 0) return 'no-copy';
  if (line.copies_withdrawn >= line.copies_provided) return 'every-copy-withdrawn';
  return 'reached-no-one';
}
